import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  DENIED_CONSENT,
  GRANTED_ALL_CONSENT,
  persistConsent,
  preferencesToConsent,
  readSavedConsent,
  updateConsent,
} from "@/consent/consent";

const DEFAULT_PREFS = {
  necessary: true,
  analytics: false,
  ads: false,
  personalization: false,
};

export default function CookieBanner({ open, setOpen, forceOpenModal, setForceOpenModal }) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const saved = readSavedConsent();
    if (!saved) {
      setOpen(true);
    } else if (saved.preferences) {
      setPrefs({ ...DEFAULT_PREFS, ...saved.preferences });
    }
  }, [setOpen]);

  useEffect(() => {
    if (forceOpenModal) {
      setShowModal(true);
      setOpen(true);
      setForceOpenModal(false);
    }
  }, [forceOpenModal, setForceOpenModal, setOpen]);

  const acceptAll = () => {
    const newPrefs = { necessary: true, analytics: true, ads: true, personalization: true };
    setPrefs(newPrefs);
    updateConsent(GRANTED_ALL_CONSENT);
    persistConsent({ preferences: newPrefs, consent: GRANTED_ALL_CONSENT });
    setOpen(false);
    setShowModal(false);
  };

  const rejectAll = () => {
    const newPrefs = { necessary: true, analytics: false, ads: false, personalization: false };
    setPrefs(newPrefs);
    updateConsent(DENIED_CONSENT);
    persistConsent({ preferences: newPrefs, consent: DENIED_CONSENT });
    setOpen(false);
    setShowModal(false);
  };

  const savePreferences = () => {
    const consent = preferencesToConsent(prefs);
    updateConsent(consent);
    persistConsent({ preferences: prefs, consent });
    setOpen(false);
    setShowModal(false);
  };

  const togglePref = (key) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  if (!open) return null;

  return (
    <>
      {!showModal && (
        <div className="cookie-banner" role="dialog" aria-live="polite" aria-label={t("cookies.banner.title")} data-testid="cookie-banner">
          <div className="cookie-banner-inner">
            <div className="cookie-banner-text">
              <h3 className="cookie-banner-title">{t("cookies.banner.title")}</h3>
              <p className="cookie-banner-body">{t("cookies.banner.text")}</p>
            </div>
            <div className="cookie-banner-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowModal(true)} data-testid="cookie-manage">
                {t("cookies.banner.manage")}
              </button>
              <button type="button" className="btn btn-outline" onClick={rejectAll} data-testid="cookie-reject">
                {t("cookies.banner.rejectAll")}
              </button>
              <button type="button" className="btn btn-solid" onClick={acceptAll} data-testid="cookie-accept">
                {t("cookies.banner.acceptAll")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="cookie-modal-backdrop" role="dialog" aria-modal="true" aria-label={t("cookies.prefs.title")} data-testid="cookie-modal">
          <div className="cookie-modal">
            <div className="cookie-modal-header">
              <h3 className="cookie-modal-title">{t("cookies.prefs.title")}</h3>
              <button type="button" className="icon-btn" onClick={() => setShowModal(false)} aria-label={t("cookies.banner.close")}>
                <X size={20} />
              </button>
            </div>
            <p className="cookie-modal-intro">{t("cookies.prefs.intro")}</p>

            <div className="cookie-prefs">
              <PrefRow
                title={t("cookies.prefs.necessaryLabel")}
                text={t("cookies.prefs.necessaryText")}
                checked={true}
                disabled
              />
              <PrefRow
                title={t("cookies.prefs.analyticsLabel")}
                text={t("cookies.prefs.analyticsText")}
                checked={prefs.analytics}
                onChange={() => togglePref("analytics")}
                testId="pref-analytics"
              />
              <PrefRow
                title={t("cookies.prefs.adsLabel")}
                text={t("cookies.prefs.adsText")}
                checked={prefs.ads}
                onChange={() => togglePref("ads")}
                testId="pref-ads"
              />
              <PrefRow
                title={t("cookies.prefs.personalizationLabel")}
                text={t("cookies.prefs.personalizationText")}
                checked={prefs.personalization}
                onChange={() => togglePref("personalization")}
                testId="pref-personalization"
              />
            </div>

            <div className="cookie-modal-actions">
              <button type="button" className="btn btn-outline" onClick={rejectAll} data-testid="pref-reject-all">
                {t("cookies.banner.rejectAll")}
              </button>
              <button type="button" className="btn btn-outline" onClick={acceptAll} data-testid="pref-accept-all">
                {t("cookies.banner.acceptAll")}
              </button>
              <button type="button" className="btn btn-solid" onClick={savePreferences} data-testid="pref-save">
                {t("cookies.banner.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PrefRow({ title, text, checked, onChange, disabled, testId }) {
  return (
    <label className={`pref-row ${disabled ? "pref-row-disabled" : ""}`}>
      <div className="pref-row-text">
        <span className="pref-row-title">{title}</span>
        <span className="pref-row-body">{text}</span>
      </div>
      <span className={`switch ${checked ? "switch-on" : ""} ${disabled ? "switch-disabled" : ""}`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          data-testid={testId}
        />
        <span className="switch-track"><span className="switch-thumb" /></span>
      </span>
    </label>
  );
}
